import type { ComponentType } from 'react';
import { HeroSlider } from './components/HeroSlider';
import {
  Article,
  Counters,
  CTA,
  ContactInfo,
  Features,
  ImageText,
  MapEmbed,
  PageBanner,
  RichText,
  ServiceTabs,
  Team,
  Testimonials,
  TrackingEvent,
} from './components/blocks';
import { ContactForm } from './components/ContactForm';
import { AppointmentCalendar } from './components/AppointmentCalendar';
import { blockMeta, type BlockMeta } from './blockMeta.ts';
import HeroSliderSchema from '../schemas/blocks/HeroSlider.v1.json';
import PageBannerSchema from '../schemas/blocks/PageBanner.v1.json';
import ServiceTabsSchema from '../schemas/blocks/ServiceTabs.v1.json';
import FeaturesSchema from '../schemas/blocks/Features.v1.json';
import CTASchema from '../schemas/blocks/CTA.v1.json';
import TestimonialsSchema from '../schemas/blocks/Testimonials.v1.json';
import TeamSchema from '../schemas/blocks/Team.v1.json';
import ArticleSchema from '../schemas/blocks/Article.v1.json';
import RichTextSchema from '../schemas/blocks/RichText.v1.json';
import ImageTextSchema from '../schemas/blocks/ImageText.v1.json';
import CountersSchema from '../schemas/blocks/Counters.v1.json';
import ContactInfoSchema from '../schemas/blocks/ContactInfo.v1.json';
import ContactFormSchema from '../schemas/blocks/ContactForm.v1.json';
import AppointmentCalendarSchema from '../schemas/blocks/AppointmentCalendar.v1.json';
import MapEmbedSchema from '../schemas/blocks/MapEmbed.v1.json';
import TrackingEventSchema from '../schemas/blocks/TrackingEvent.v1.json';

export type JsonSchema = Record<string, unknown> & {
  title?: string;
  type?: string | string[];
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  required?: string[];
  enum?: unknown[];
  'x-ui'?: string;
  'x-itemTitle'?: string;
};

export interface BlockDefinition extends BlockMeta {
  type: string;
  schema: JsonSchema;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
}

const components: Record<string, BlockDefinition['component']> = {
  HeroSlider: HeroSlider,
  PageBanner: PageBanner,
  ServiceTabs: ServiceTabs,
  Features: Features,
  CTA: CTA,
  Testimonials: Testimonials,
  Team: Team,
  Article: Article,
  RichText: RichText,
  ImageText: ImageText,
  Counters: Counters,
  ContactInfo: ContactInfo,
  ContactForm: ContactForm,
  AppointmentCalendar: AppointmentCalendar,
  MapEmbed: MapEmbed,
  TrackingEvent: TrackingEvent,
};

const schemas: Record<string, JsonSchema> = {
  HeroSlider: HeroSliderSchema as JsonSchema,
  PageBanner: PageBannerSchema as JsonSchema,
  ServiceTabs: ServiceTabsSchema as JsonSchema,
  Features: FeaturesSchema as JsonSchema,
  CTA: CTASchema as JsonSchema,
  Testimonials: TestimonialsSchema as JsonSchema,
  Team: TeamSchema as JsonSchema,
  Article: ArticleSchema as JsonSchema,
  RichText: RichTextSchema as JsonSchema,
  ImageText: ImageTextSchema as JsonSchema,
  Counters: CountersSchema as JsonSchema,
  ContactInfo: ContactInfoSchema as JsonSchema,
  ContactForm: ContactFormSchema as JsonSchema,
  AppointmentCalendar: AppointmentCalendarSchema as JsonSchema,
  MapEmbed: MapEmbedSchema as JsonSchema,
  TrackingEvent: TrackingEventSchema as JsonSchema,
};

/**
 * The controlled component registry. Only these versioned types can be placed on pages; the same entries are used
 * by the SSR renderer, the admin preview and (through the JSON schema files) by the server-side validation.
 */
export const blockRegistry: Record<string, BlockDefinition> = Object.fromEntries(
  Object.entries(blockMeta).map(([type, meta]) => [type, { type, ...meta, schema: schemas[type], component: components[type] }]),
);

export function getBlockDefinition(type: string, version: number): BlockDefinition | undefined {
  const def = blockRegistry[type];
  return def && def.version === version ? def : undefined;
}
